import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Images from '../assets/AllImages';
import { useTranslation } from 'react-i18next';
import { FaPhoneAlt, FaDownload, FaLaptop, FaSearchPlus, FaTimes } from 'react-icons/fa';

const PHONE = '048334000';
const PHONE_DISPLAY = '048 334 000';

// Price row helper: { label, was?, now, suffix? }
const P = (label, now, was, suffix) => ({ label, now, was, suffix });

// Download a single poster as a file (saves to device on Android/iPhone).
const downloadOffer = async (offer) => {
  try {
    const res = await fetch(offer.img);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = offer.download;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    window.open(offer.img, '_blank');
  }
};

// STEP OUTDOOR offers — poster image + full written details & prices.
const outdoorOffers = (t) => [
  {
    img: Images.OfferShkollaVeroreChildren,
    download: 'Step-Arena-Shkolla-Verore-Femije.jpg',
    title: t('offers.outdoor.summerKids'),
    tag: t('offers.tag.kids'),
    groups: [
      {
        title: t('offers.summerKids.g1.title'),
        time: t('offers.summerKids.g1.time'),
        prices: [
          P(t('offers.label.weekly'), '30€', '35€'),
          P(t('offers.label.monthly'), '65€', '75€'),
          P(t('offers.label.seasonal'), '125€', '150€'),
        ],
      },
      {
        title: t('offers.summerKids.g2.title'),
        time: t('offers.summerKids.g2.time'),
        prices: [
          P(t('offers.label.monthly'), '50€', '60€'),
          P(t('offers.label.seasonal'), '99€', '120€'),
        ],
      },
    ],
  },
  {
    img: Images.OfferShkollaVeroreAdults,
    download: 'Step-Arena-Shkolla-Verore-Te-Rritur.jpg',
    title: t('offers.outdoor.summerAdults'),
    tag: t('offers.tag.adults'),
    groups: [
      {
        title: t('offers.summerAdults.g1.title'),
        time: t('offers.summerAdults.g1.time'),
        prices: [
          P(t('offers.label.monthly'), '59€', '70€'),
          P(t('offers.label.seasonal'), '135€', '160€'),
        ],
      },
      {
        title: t('offers.summerAdults.g2.title'),
        time: t('offers.summerAdults.g2.time'),
        prices: [
          P(t('offers.label.monthly'), '59€', '70€'),
          P(t('offers.label.seasonal'), '135€', '160€'),
        ],
      },
    ],
  },
  {
    img: Images.OfferPakoIndividuale,
    download: 'Step-Arena-Pako-Individuale.jpg',
    title: t('offers.outdoor.individual'),
    groups: [
      {
        title: t('offers.individual.g1.title'),
        prices: [
          P(t('offers.individual.adultUnlimited'), '119€', '140€', t('offers.perMonth')),
          P(t('offers.individual.adult2'), '55€', '65€', t('offers.perMonth')),
          P(t('offers.label.under14'), '59€', '70€', t('offers.perMonth')),
        ],
      },
      {
        title: t('offers.individual.g2.title'),
        prices: [
          P(t('offers.label.adult'), '240€', '290€'),
          P(t('offers.label.under14'), '140€', '170€'),
        ],
      },
    ],
  },
  {
    img: Images.OfferHyrjaDitore,
    download: 'Step-Arena-Hyrja-Ditore.jpg',
    title: t('offers.outdoor.daily'),
    groups: [
      {
        title: t('offers.daily.g1.title'),
        prices: [
          P(t('offers.daily.weekdays'), '20€'),
          P(t('offers.daily.weekend'), '25€'),
        ],
      },
    ],
    includes: [t('offers.daily.include1'), t('offers.daily.include2')],
  },
];

const PriceRow = ({ row }) => {
  // Show the original price only (the struck-through `was` value when present), no sale price.
  const price = row.was || row.now;
  return (
    <div className="d-flex justify-content-between align-items-baseline py-2" style={{ borderBottom: '1px dashed #eef1ff' }}>
      <span className="fw-semibold" style={{ color: '#2b3146', fontSize: '1.1rem' }}>{row.label}</span>
      <span className="text-nowrap ps-3">
        <strong style={{ color: '#1D3EE3', fontSize: '1.3rem' }}>
          {price}{row.suffix ? <span style={{ fontSize: '0.9rem', fontWeight: 700 }}>{row.suffix}</span> : null}
        </strong>
      </span>
    </div>
  );
};

const OfferCard = ({ offer, t, onOpen }) => {
  return (
    <div
      className="h-100 d-flex flex-column"
      style={{
        borderRadius: '20px',
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: '0 10px 30px rgba(29, 62, 227, 0.10)',
        border: '1px solid #eef1ff',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-6px)';
        e.currentTarget.style.boxShadow = '0 18px 40px rgba(29, 62, 227, 0.22)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 10px 30px rgba(29, 62, 227, 0.10)';
      }}
    >
      {/* Clickable poster image — opens full-quality popup */}
      <button
        type="button"
        onClick={onOpen}
        className="d-block position-relative border-0 p-0 w-100"
        style={{ backgroundColor: '#f7f9ff', overflow: 'hidden', cursor: 'zoom-in' }}
        title={t('offers.viewPoster')}
      >
        <img
          src={offer.img}
          alt={offer.title}
          className="w-100"
          style={{ display: 'block', height: '260px', objectFit: 'cover', objectPosition: 'top center' }}
        />
        <span
          className="position-absolute d-flex align-items-center gap-2 fw-semibold"
          style={{
            bottom: '12px',
            right: '12px',
            backgroundColor: 'rgba(15, 28, 77, 0.78)',
            color: '#fff',
            borderRadius: '999px',
            padding: '6px 12px',
            fontSize: '0.78rem',
          }}
        >
          <FaSearchPlus size={13} /> {t('offers.viewPoster')}
        </span>
      </button>

      {/* Body: title + written details/prices */}
      <div className="p-4 d-flex flex-column flex-grow-1">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="fw-bold mb-0 pe-2" style={{ color: '#0f1c4d', fontSize: '1.2rem', lineHeight: 1.3 }}>
            {offer.title}
          </h4>
          {offer.tag && (
            <span
              className="fw-bold text-uppercase"
              style={{
                backgroundColor: '#eef1ff',
                color: '#1D3EE3',
                borderRadius: '8px',
                padding: '4px 10px',
                fontSize: '0.72rem',
                whiteSpace: 'nowrap',
              }}
            >
              {offer.tag}
            </span>
          )}
        </div>

        {offer.groups.map((group, gi) => (
          <div key={gi} className="mb-3">
            <div className="fw-bold" style={{ color: '#0f1c4d', fontSize: '1.2rem' }}>{group.title}</div>
            {group.time && (
              <div className="mb-2" style={{ color: '#7c8298', fontSize: '0.95rem', fontWeight: 500 }}>{group.time}</div>
            )}
            {group.prices.map((row, ri) => (
              <PriceRow key={ri} row={row} />
            ))}
          </div>
        ))}

        {offer.includes && (
          <div className="mb-3">
            <div className="fw-semibold mb-1" style={{ color: '#0f1c4d', fontSize: '0.95rem' }}>
              {t('offers.includesLabel')}
            </div>
            <div className="d-flex flex-wrap gap-2">
              {offer.includes.map((item, ii) => (
                <span
                  key={ii}
                  style={{
                    backgroundColor: '#f0f4ff',
                    color: '#1D3EE3',
                    borderRadius: '999px',
                    padding: '4px 12px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                  }}
                >
                  ✓ {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="mt-auto pt-2 d-grid gap-2">
          {/* Primary: call reception to register */}
          <a
            href={`tel:${PHONE}`}
            className="d-flex align-items-center justify-content-center gap-2 fw-bold text-white"
            style={{
              backgroundColor: '#1D3EE3',
              borderRadius: '12px',
              padding: '12px 16px',
              textDecoration: 'none',
              fontSize: '1rem',
              transition: 'background-color 0.25s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1530b8')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#1D3EE3')}
          >
            <FaPhoneAlt size={15} />
            {t('offers.btnRegister')}
          </a>

          <div className="d-grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
            {/* Download poster */}
            <a
              href={offer.img}
              download={offer.download}
              className="d-flex align-items-center justify-content-center gap-2 fw-semibold"
              style={{
                backgroundColor: '#fff',
                color: '#1D3EE3',
                border: '1.5px solid #1D3EE3',
                borderRadius: '12px',
                padding: '11px 12px',
                textDecoration: 'none',
                fontSize: '0.92rem',
                transition: 'background-color 0.25s ease, color 0.25s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1D3EE3'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#1D3EE3'; }}
            >
              <FaDownload size={14} />
              {t('offers.btnDownload')}
            </a>

            {/* Online registration — coming soon (disabled) */}
            <button
              type="button"
              disabled
              className="d-flex align-items-center justify-content-center gap-2 fw-semibold"
              style={{
                backgroundColor: '#f0f1f5',
                color: '#9aa0ad',
                border: '1px solid #e3e5ec',
                borderRadius: '12px',
                padding: '11px 12px',
                fontSize: '0.85rem',
                cursor: 'not-allowed',
              }}
              title={t('offers.btnOnlineSoon')}
            >
              <FaLaptop size={14} />
              {t('offers.btnOnlineShort')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const OFERTAT = () => {
  const { t } = useTranslation();
  const offers = outdoorOffers(t);

  const [lightbox, setLightbox] = useState(null);

  // Download every offer poster one by one (saves to device on Android/iPhone).
  const downloadAll = async () => {
    for (const offer of offers) {
      await downloadOffer(offer);
      // small gap so mobile browsers handle each save separately
      await new Promise((r) => setTimeout(r, 700));
    }
  };

  return (
    <>
      {/* Hero banner */}
      <main>
        <div className="container-fluid bg-primary py-3 py-md-0">
          <div className="row align-items-center">
            <div className="col-12 col-md-6 px-5">
              <h1 className="text-white fw-bold mb-4 responsive-heading">{t('offers.title')}</h1>
            </div>
            <div className="col-12 col-md-6 px-5 d-none d-sm-block">
              <img src={Images.Vactor2} alt="" className="w-50 float-end" />
            </div>
          </div>
        </div>
      </main>

      <div className="container my-5">
        {/* STEP OUTDOOR section */}
        <div className="text-center mb-4">
          <span
            className="d-inline-block fw-bold text-uppercase mb-2"
            style={{ color: '#1D3EE3', letterSpacing: '0.14em', fontSize: '1.05rem' }}
          >
            {t('offers.outdoor.kicker')}
          </span>
          <h2 className="fw-bold mb-3" style={{ color: '#0f1c4d', fontSize: 'clamp(2.6rem, 6vw, 4rem)', lineHeight: 1.05, letterSpacing: '0.01em' }}>
            {t('offers.outdoor.title')}
          </h2>
          <p className="text-muted mx-auto" style={{ maxWidth: '660px', fontSize: '1.2rem' }}>
            {t('offers.outdoor.subtitle')}
          </p>
          <button
            type="button"
            onClick={downloadAll}
            className="d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-white mt-2"
            style={{
              backgroundColor: '#1D3EE3',
              border: 'none',
              borderRadius: '999px',
              padding: '12px 26px',
              fontSize: '1rem',
              boxShadow: '0 8px 20px rgba(29, 62, 227, 0.25)',
              transition: 'background-color 0.25s ease, transform 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1530b8'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1D3EE3'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <FaDownload size={16} />
            {t('offers.btnDownloadAll')}
          </button>
        </div>

        <div className="row g-4">
          {offers.map((offer, i) => (
            <div className="col-12 col-md-6" key={i}>
              <OfferCard offer={offer} t={t} onOpen={() => setLightbox(offer)} />
            </div>
          ))}
        </div>

        {/* Reception contact note */}
        <p className="text-center mt-4 mb-0" style={{ fontSize: '1.05rem', color: '#3a4156' }}>
          {t('offers.receptionNote')}{' '}
          <a href={`tel:${PHONE}`} className="fw-bold" style={{ color: '#1D3EE3', textDecoration: 'none' }}>
            {PHONE_DISPLAY}
          </a>
        </p>

        {/* STEP INDOOR — coming soon */}
        <div
          className="text-center mt-5 p-5"
          style={{
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #1D3EE3 0%, #2f57f0 100%)',
            color: '#fff',
          }}
        >
          <span
            className="d-inline-block fw-bold text-uppercase mb-3 px-3 py-1"
            style={{ background: 'rgba(255,255,255,0.18)', borderRadius: '999px', letterSpacing: '0.1em', fontSize: '0.8rem' }}
          >
            {t('offers.indoor.badge')}
          </span>
          <h2 className="fw-bold mb-2" style={{ fontSize: '2rem' }}>
            {t('offers.indoor.title')}
          </h2>
          <p className="mb-0 mx-auto" style={{ maxWidth: '560px', opacity: 0.9, fontSize: '1.1rem' }}>
            {t('offers.indoor.subtitle')}
          </p>
        </div>
      </div>

      {/* Full-quality poster popup (lightbox) — portaled to body so it pins to the viewport */}
      {lightbox && createPortal(
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(8, 14, 40, 0.88)',
            zIndex: 3000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            backdropFilter: 'blur(3px)',
          }}
        >
          {/* Close (X) */}
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label={t('offers.btnClose')}
            className="d-flex align-items-center justify-content-center"
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            <FaTimes size={20} />
          </button>

          <div
            onClick={(e) => e.stopPropagation()}
            className="d-flex flex-column align-items-center"
            style={{ maxWidth: '760px', width: '100%', maxHeight: '88vh' }}
          >
            <img
              src={lightbox.img}
              alt={lightbox.title}
              style={{
                maxWidth: '100%',
                minHeight: 0,
                flex: '1 1 auto',
                objectFit: 'contain',
                borderRadius: '14px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              }}
            />
            <div className="d-flex gap-2 mt-3 flex-wrap justify-content-center flex-shrink-0">
              <button
                type="button"
                onClick={() => downloadOffer(lightbox)}
                className="d-flex align-items-center justify-content-center gap-2 fw-bold text-white"
                style={{
                  backgroundColor: '#1D3EE3',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  fontSize: '1rem',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1530b8')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#1D3EE3')}
              >
                <FaDownload size={16} />
                {t('offers.btnDownload')}
              </button>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="d-flex align-items-center justify-content-center gap-2 fw-semibold"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  fontSize: '1rem',
                }}
              >
                <FaTimes size={15} />
                {t('offers.btnClose')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default OFERTAT;
